import styled from "styled-components";

export const ThemesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 35px);
  gap: 12px;
  justify-content: start;
`;
export const ThemeBox = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #fff;
  cursor: pointer;
  background-color: ${({ color }) => color || `#D4F4EE`};
  &:hover {
    border-color: #4d94fb;
  }
`;
export const WeekDaysContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 45px);
  gap: 10px;
  justify-content: start;
`;
export const WeekdayBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  border-radius: 8px;
  border: ${({ active, theme }) =>
    active
      ? `1px solid ${theme.colors.main.primary}`
      : `1px solid ${theme.colors.border.secondary}`};
  font-size: 12px;
  font-weight: 500;
  color: ${({ active, theme }) =>
    active ? theme.colors.main.white : theme.colors.main.textMain};
  background-color: ${({ active, theme }) =>
    active ? theme.colors.main.primary : theme.colors.main.white};
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.main.primary};
    box-shadow: ${({ theme }) => `0 0 0 4px ${theme.colors.main.primaryLight}`};
  }
`;
export const AdditionalOptionText = styled.p`
  margin: 0 30px;
  position: relative;
  top: -11px;
  background-color: ${({ theme }) => theme.colors.main.white};
  padding: 0 10px;
  color: ${({ theme }) => theme.font.pageText.colorSecondary};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

export const RepeatSection = styled.div`
  display: grid;
  grid-template-columns: minmax(230px, 260px);
  gap: 15px;
  padding-left: 40px;
  margin: 7px 0 0 2px;
  justify-content: start;
  align-content: start;
  border-left: ${({ theme }) =>
    `2px solid ${theme.colors.border.secondaryLight}`};
`;

/**
 * MonthDateContainer - Always 7 columns calendar grid
 * - Fixed to 7 columns (standard calendar week)
 * - Responsive via cell size adjustments, gap stays constant
 * - Desktop: wider cells, Tablet: medium, Mobile: compact
 */
export const MonthDateContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  justify-content: center;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    /* Tablet: adjust container width */
    max-width: 550px;
  }

  @media (max-width: 768px) {
    /* Tablet small: more compact container */
    max-width: 480px;
  }

  @media (max-width: 480px) {
    /* Mobile: full width with padding */
    max-width: 100%;
    padding: 0 8px;
  }
`;

/**
 * MonthDateBox - Responsive rectangular date cell
 * - Fixed height (40px desktop, 44px mobile) for consistent layout
 * - Width determined by 7-column grid (1fr each)
 * - Spanning "Last Day" cell stretches to fill remaining columns
 * - Touch-friendly sizing on mobile
 */
export const MonthDateBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  border-radius: 5px;
  border: ${({ active, theme }) =>
    active
      ? `1px solid ${theme.colors.main.primary}`
      : `1px solid ${theme.colors.border.secondary}`};
  font-size: 12px;
  font-weight: 600;
  color: ${({ active, theme }) =>
    active ? theme.colors.main.white : theme.colors.main.textMain};
  background-color: ${({ active, theme }) =>
    active ? theme.colors.main.primary : theme.colors.main.white};
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 4px;

  /* Spanning behavior - only apply on larger screens */
  grid-column: ${({ spanColumns }) =>
    spanColumns && spanColumns > 1 ? `span ${spanColumns}` : "auto"};

  /* Mobile: touch-friendly height */
  @media (max-width: 480px) {
    height: 44px;
    font-size: 11px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.main.primary};
    box-shadow: ${({ theme }) => `0 0 0 4px ${theme.colors.main.primaryLight}`};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const WeekNumberBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 35px;
  height: 35px;
`;
export const ScheduleText = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  padding: 10px 15px;
  border-radius: 4px;
  background-color: #e4e7ebb0;
  font-style: italic;
  font-size: 12px;
  min-width: 370px;
  border-left: ${({ theme }) => `10px solid ${theme.colors.main.primary}`};
`;
export const DurationContainer = styled.div`
  display: flex;
  gap: 10px;
`;
export const DurationText = styled.p`
  color: ${({ color, theme }) => color || theme.colors.main.primary};
  font-size: 14px;
  margin: 0;
`;

export const CheckCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid
    ${({ active, theme }) =>
      active ? theme.colors.main.white : theme.colors.border.secondaryLight};
  background-color: ${({ active, theme }) =>
    active ? theme.colors.main.white : theme.colors.main.white};
`;

export const EditTypeButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 40px;
  padding: 0 20px;
  border-radius: ${({ theme }) => theme.button.borderRadius};
  border: 1px solid
    ${({ active, theme }) =>
      active ? theme.colors.main.primary : theme.colors.border.secondaryLight};
  background-color: ${({ active, theme }) =>
    active ? theme.colors.main.primary : theme.colors.main.white};
  color: ${({ active, theme }) =>
    active ? theme.colors.main.white : theme.colors.main.textMain};
  font-size: 13px;
  font-weight: ${({ active, theme }) =>
    active ? theme.fontWeights.semibold : theme.fontWeights.regular};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.main.primary};
  }
`;

export const TickIcon = styled.img`
  width: 14px;
  height: 14px;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: opacity 0.2s ease;
`;
